import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { Link } from 'react-router-dom';
import { AnimatedSection } from '../../components/AnimatedSection';

export default function SecurityPage() {
  const { t } = useTranslation();

  const securityServices = [
    {
      icon: 'ri-shield-flash-line',
      title: t('sec_s1_title'),
      badge: t('sec_s1_badge'),
      desc: t('sec_s1_desc'),
      features: [t('sec_s1_f1'), t('sec_s1_f2'), t('sec_s1_f3'), t('sec_s1_f4')],
      color: 'border-emerald-500/30 hover:border-emerald-500/60',
      iconBg: 'bg-emerald-500/10',
      iconColor: 'text-emerald-400',
    },
    {
      icon: 'ri-shield-keyhole-line',
      title: t('sec_s2_title'),
      badge: t('sec_s2_badge'),
      desc: t('sec_s2_desc'),
      features: [t('sec_s2_f1'), t('sec_s2_f2'), t('sec_s2_f3'), t('sec_s2_f4')],
      color: 'border-cyan-500/30 hover:border-cyan-500/60',
      iconBg: 'bg-cyan-500/10',
      iconColor: 'text-cyan-400',
    },
    {
      icon: 'ri-radar-line',
      title: t('sec_s3_title'),
      badge: t('sec_s3_badge'),
      desc: t('sec_s3_desc'),
      features: [t('sec_s3_f1'), t('sec_s3_f2'), t('sec_s3_f3'), t('sec_s3_f4')],
      color: 'border-orange-500/30 hover:border-orange-500/60',
      iconBg: 'bg-orange-500/10',
      iconColor: 'text-orange-400',
    },
    {
      icon: 'ri-bug-line',
      title: t('sec_s4_title'),
      badge: t('sec_s4_badge'),
      desc: t('sec_s4_desc'),
      features: [t('sec_s4_f1'), t('sec_s4_f2'), t('sec_s4_f3'), t('sec_s4_f4')],
      color: 'border-red-500/30 hover:border-red-500/60',
      iconBg: 'bg-red-500/10',
      iconColor: 'text-red-400',
    },
  ];

  const threats = [
    { icon: 'ri-virus-line', name: t('sec_t1_name'), stat: t('sec_t1_stat'), color: 'text-red-400' },
    { icon: 'ri-mail-forbid-line', name: t('sec_t2_name'), stat: t('sec_t2_stat'), color: 'text-orange-400' },
    { icon: 'ri-server-line', name: t('sec_t3_name'), stat: t('sec_t3_stat'), color: 'text-yellow-400' },
    { icon: 'ri-spy-line', name: t('sec_t4_name'), stat: t('sec_t4_stat'), color: 'text-purple-400' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/aa658fa24eb95b8960f61b7250f91f93.jpg"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <AnimatedSection animation="fade-in">
            <nav className="flex items-center gap-2 text-slate-400 text-sm mb-8">
              <Link to="/" className="hover:text-cyan-400 cursor-pointer">{t('sec_breadcrumb_home')}</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">{t('sec_breadcrumb_current')}</span>
            </nav>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-6">
              <i className="ri-shield-keyhole-line text-emerald-400 text-sm"></i>
              <span className="text-emerald-400 text-sm font-medium">{t('sec_badge')}</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
              {t('sec_title1')}<br />
              <span className="text-emerald-400">{t('sec_title2')}</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl leading-relaxed mb-8">
              {t('sec_desc')}
            </p>
            <div className="flex gap-4">
              <Link to="/contact" className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-6 py-3 rounded-lg transition-all whitespace-nowrap cursor-pointer">
                {t('sec_cta1')}
              </Link>
              <Link to="/case-studies" className="border border-slate-600 hover:border-emerald-500 text-white hover:text-emerald-400 px-6 py-3 rounded-lg transition-all whitespace-nowrap cursor-pointer">
                {t('sec_cta2')}
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Threats */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection animation="fade-in">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-white mb-2">{t('sec_threats_title')}</h2>
              <p className="text-slate-400 text-sm">{t('sec_threats_sub')}</p>
            </div>
          </AnimatedSection>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {threats.map((threat, i) => (
              <AnimatedSection key={threat.name} animation="fade-in" delay={i * 0.08}>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <i className={`${threat.icon} text-3xl ${threat.color}`}></i>
                  </div>
                  <h4 className="text-white font-semibold text-sm mb-1">{threat.name}</h4>
                  <p className="text-slate-500 text-xs">{threat.stat}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Security Services */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection animation="fade-in">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-white mb-3">{t('sec_svc_title')}</h2>
              <p className="text-slate-400">{t('sec_svc_sub')}</p>
            </div>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-8">
            {securityServices.map((svc, i) => (
              <AnimatedSection key={svc.title} animation="fade-in" delay={i * 0.1}>
                <div className={`bg-slate-800/40 border rounded-2xl p-8 transition-all ${svc.color}`}>
                  <div className="flex items-start gap-4 mb-5">
                    <div className={`w-12 h-12 flex items-center justify-center ${svc.iconBg} rounded-xl shrink-0`}>
                      <i className={`${svc.icon} ${svc.iconColor} text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">{svc.title}</h3>
                      <p className="text-slate-500 text-xs mt-0.5">{svc.badge}</p>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed mb-5">{svc.desc}</p>
                  <ul className="grid grid-cols-2 gap-2">
                    {svc.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-slate-300 text-xs">
                        <i className="ri-checkbox-circle-line text-emerald-500"></i>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-16 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection animation="fade-in">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 bg-slate-800/50 border border-emerald-500/20 rounded-2xl p-8">
              <div>
                <h3 className="text-white font-bold text-xl mb-2">{t('sec_compliance_title')}</h3>
                <p className="text-slate-400 text-sm max-w-lg">{t('sec_compliance_desc')}</p>
              </div>
              <div className="flex flex-wrap gap-3 shrink-0">
                {['GDPR', 'PCI DSS', 'ISO 27001', 'SOC 2'].map(cert => (
                  <span key={cert} className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm font-semibold rounded-lg whitespace-nowrap">
                    {cert}
                  </span>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-950 border-t border-slate-800">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">{t('sec_cta_title')}</h2>
          <p className="text-slate-400 mb-8">{t('sec_cta_desc')}</p>
          <Link to="/contact" className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-8 py-4 rounded-lg transition-all whitespace-nowrap cursor-pointer">
            {t('sec_cta_btn')}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
